import React from "react";
import Editor from "@monaco-editor/react";
import { X, Sparkles, ArrowRight, Check } from "lucide-react";
import { FaPython, FaJsSquare } from "react-icons/fa";
import { SiArduino } from "react-icons/si";
import { TbBrandCpp } from "react-icons/tb";

const ConvertSnippetDialog = ({
  isOpen,
  onClose,
  snippet,
  sourceLang = "python",
  targetLang = "javascript",
  convertedCode = "",
  onConfirmConvertAndInsert,
  onConfirmRawInsert,
  theme = "dark",
}) => {
  if (!isOpen || !snippet) return null;

  const isLight = theme === "light";
  const getLangDisplayName = (lang) => {
    if (lang === "javascript") return "JavaScript";
    if (lang === "python") return "Python";
    if (lang === "cpp") return "C++";
    if (lang === "arduino") return "Arduino C++";
    if (lang === "scratch" || lang === "junior") return "Scratch";
    return lang ? lang.toUpperCase() : "Code";
  };

  const sourceName = getLangDisplayName(sourceLang);
  const targetName = getLangDisplayName(targetLang);

  const renderLangIcon = (lang) => {
    if (lang === "javascript") return <FaJsSquare className="w-4 h-4 text-yellow-500" />;
    if (lang === "cpp") return <TbBrandCpp className="w-4 h-4 text-cyan-400" />;
    if (lang === "arduino") return <SiArduino className="w-4 h-4 text-teal-400" />;
    return <FaPython className="w-4 h-4 text-blue-500" />;
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className={`w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden font-sans border-0 ${
          isLight
            ? "bg-white ring-1 ring-neutral-200 text-neutral-800"
            : "bg-[#13151b] ring-1 ring-[#27272a] text-white shadow-[0_25px_60px_rgba(0,0,0,0.9)]"
        }`}
      >
        {/* Header */}
        <div
          className={`px-5 py-4 flex items-center justify-between border-0 ${
            isLight
              ? "bg-neutral-50 border-b border-neutral-200"
              : "bg-[#0c0d12] border-b border-[#222226]"
          }`}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-purple-600/15 ring-1 ring-purple-500/25 flex items-center justify-center text-purple-400">
              <Sparkles className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <h3 className={`font-bold text-base leading-tight ${isLight ? "text-neutral-900" : "text-white"}`}>
                Convert Snippet for {targetName}?
              </h3>
              <p className={`text-xs ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                Cross-language snippet helper
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
              isLight
                ? "bg-neutral-100 hover:bg-neutral-200 text-neutral-500"
                : "bg-white/5 hover:bg-white/10 text-neutral-300"
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 space-y-3.5">
          {/* Informational Message */}
          <div
            className={`p-3 rounded-xl text-xs leading-relaxed text-left border-0 ${
              isLight
                ? "bg-amber-50 text-amber-900 ring-1 ring-amber-200/80"
                : "bg-amber-500/10 text-amber-200/90 ring-1 ring-amber-500/20"
            }`}
          >
            The snippet is in <span className="font-bold underline decoration-amber-400/50">{sourceName}</span>, and cannot be inserted in <span className="font-bold underline decoration-amber-400/50">{targetName}</span> environment, you can convert the snippet to <span className="font-bold text-white">{targetName}</span> to insert.
          </div>

          {/* Symmetric Single-Line Conversion Flow Row */}
          <div
            className={`p-2.5 px-3.5 rounded-xl flex items-center justify-between gap-2 border-0 ${
              isLight
                ? "bg-neutral-100/80 ring-1 ring-neutral-200"
                : "bg-[#0a0b0f] ring-1 ring-[#27272a]"
            }`}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-6 h-6 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                {renderLangIcon(sourceLang)}
              </div>
              <span className="text-xs font-bold truncate">{sourceName} Snippet</span>
            </div>

            <div className="flex items-center justify-center gap-1.5 px-3 py-1 rounded-full bg-purple-600/15 ring-1 ring-purple-500/30 text-purple-400 text-[11px] font-bold flex-shrink-0">
              <span>Auto-Convert</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </div>

            <div className="flex items-center justify-end gap-2 flex-1 min-w-0">
              <span className="text-xs font-bold truncate text-right">{targetName} Code</span>
              <div className="w-6 h-6 rounded-lg bg-yellow-500/15 flex items-center justify-center flex-shrink-0">
                {renderLangIcon(targetLang)}
              </div>
            </div>
          </div>

          {/* Snippet Title & Description */}
          <div className="text-left space-y-0.5">
            <h4 className={`text-sm font-bold truncate ${isLight ? "text-neutral-900" : "text-white"}`}>
              {snippet.name}
            </h4>
            {snippet.description && (
              <p className={`text-xs line-clamp-2 ${isLight ? "text-neutral-600" : "text-neutral-400"}`}>
                {snippet.description}
              </p>
            )}
          </div>

          {/* Converted Code Preview */}
          <div className="space-y-1 text-left">
            <div className="flex items-center justify-between">
              <label className={`text-xs font-bold ${isLight ? "text-neutral-700" : "text-neutral-200"}`}>
                Converted {targetName} Preview
              </label>
              <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1">
                <Check className="w-3 h-3" /> Ready to insert
              </span>
            </div>

            <div
              className={`rounded-xl overflow-hidden border-0 ${
                isLight ? "ring-1 ring-neutral-200" : "ring-1 ring-[#27272a] bg-[#1e1e1e]"
              }`}
            >
              <Editor
                height={`${Math.min(140, Math.max(68, ((convertedCode || snippet.code || "").trim().split("\n").length) * 19 + 20))}px`}
                language={targetLang}
                theme={isLight ? "vs" : "vs-dark"}
                value={convertedCode || snippet.code}
                options={{
                  readOnly: true,
                  domReadOnly: true,
                  fontSize: 12,
                  lineNumbers: "off",
                  glyphMargin: false,
                  folding: false,
                  lineDecorationsWidth: 0,
                  lineNumbersMinChars: 0,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  wordWrap: "on",
                  automaticLayout: true,
                  renderLineHighlight: "none",
                  contextmenu: false,
                  padding: { top: 8, bottom: 8 },
                  scrollbar: {
                    vertical: "auto",
                    horizontal: "hidden",
                    verticalScrollbarSize: 5,
                  },
                  overviewRulerBorder: false,
                  overviewRulerLanes: 0,
                  hideCursorInOverviewRuler: true,
                }}
              />
            </div>
          </div>

          {/* Single-Line Action Buttons */}
          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition cursor-pointer border-0 ${
                isLight ? "text-neutral-600 hover:bg-neutral-100" : "text-neutral-400 hover:bg-white/5 hover:text-white"
              }`}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={onConfirmRawInsert}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition cursor-pointer border-0 ${
                isLight
                  ? "bg-neutral-100 hover:bg-neutral-200 text-neutral-700 ring-1 ring-neutral-200"
                  : "bg-white/5 hover:bg-white/10 text-neutral-300 ring-1 ring-[#27272a]"
              }`}
              title={`Insert original ${sourceName} code without converting`}
            >
              Insert Raw ({sourceName})
            </button>

            <button
              type="button"
              onClick={onConfirmConvertAndInsert}
              className="px-4 py-2 rounded-xl text-xs font-extrabold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 active:scale-95 text-white shadow-md shadow-purple-600/30 flex items-center justify-center gap-1.5 transition cursor-pointer border-0 flex-shrink-0"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              <span>Convert & Insert</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConvertSnippetDialog;
